import { customElement, property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import {
  PLANNED_DELIVERIES_API, PlannedDeliveriesApi, PlannedDelivery
} from 'components/ordering/backorders/planned-deliveries/planned-deliveries-api.js'
import { t, TranslateResult } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './planned-deliveries.style.js'


const translations: { [key: string]: () => TranslateResult } = {
  date_heading: () => t('order_date_and_status', {
    ns: 'csc-planned-deliveries',
    default: 'Order date & status',
  }),
  quantity_heading: () => t('quantity', {
    ns: 'csc-planned-deliveries',
    default: 'Quantity',
  }),
  reference_heading: () => t('order_number', {
    ns: 'csc-planned-deliveries',
    default: 'Order number',
  }),
}

const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})/

const formatDeliveryDate = (date: string): string => {
  const match = isoDatePattern.exec(date)
  if (!match) return date

  const [, year, month, day] = match
  const parsed = new Date(Number(year), Number(month) - 1, Number(day))
  if (isNaN(parsed.getTime())) return date

  return new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(parsed)
}

@customElement('csc-planned-deliveries')
export class CSCPlannedDeliveries extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, attribute: 'ecommerce-api-token-endpoint' })
  ecommerceApiTokenEndpoint!: string

  @property({ type: String, attribute: 'storefront-endpoint' })
  storefrontEndpoint!: string

  @property({ type: Boolean, attribute: 'dont-show-when-no-deliveries' })
  dontShowWhenNoDeliveries = false

  @inject(PLANNED_DELIVERIES_API)
  private plannedDeliveriesApi!: PlannedDeliveriesApi

  @state()
  deliveries: PlannedDelivery[] = []

  override async connectedCallback() {
    super.connectedCallback()

    this.deliveries = await this.plannedDeliveriesApi.get(
      this.ecommerceApiTokenEndpoint,
      this.storefrontEndpoint
    )
  }

  private _dateRenderer = (date: string, item: PlannedDelivery) => {
    return html`
      <cse-text>
        ${formatDeliveryDate(date)}
      </cse-text>
      <cse-badge variant="info">${item.status}</cse-badge>
    `
  }

  private _quantityRenderer = (quantity: string) => {
    return html`
      <cse-text>${quantity}</cse-text>
    `
  }

  private _referenceRenderer = (reference: string, item: PlannedDelivery) => {
    return html`
      <cse-link url="${item.sale_detail}">${reference}</cse-link>
    `
  }

  private _columns = [
    { name: translations['date_heading']?.(), key: 'date', width: 'auto', cellRenderer: this._dateRenderer },
    { name: translations['quantity_heading']?.(), key: 'quantity', width: 'auto', cellRenderer: this._quantityRenderer },
    { name: translations['reference_heading']?.(), key: 'sale_reference', width: 'auto', cellRenderer: this._referenceRenderer },
  ]

  override render() {
    const noDeliveries = this.deliveries.length === 0

    if (this.dontShowWhenNoDeliveries && noDeliveries) {
      return nothing
    }

    if (noDeliveries) {
      return html`
        <cse-alert variant="info">
          ${t('no_deliveries', { ns: 'csc-planned-deliveries', default: 'No deliveries' })}
        </cse-alert>
      `
    }

    return html`
      <div class="table">
        <csc-table .columns="${this._columns}" .data="${this.deliveries}" variant="line"></csc-table>
      </div>
    `
  }
}
